import React, { useCallback } from 'react';
import type { AddressResult } from '../components/map/AddressSearchBar/types';
import type { SearchHistoryItem } from '../utils/searchHistory';

interface UseSearchKeyboardProps {
  isOpen: boolean;
  results: AddressResult[];
  recentSearches: SearchHistoryItem[];
  showRecentSearches: boolean;
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  setIsOpen: (isOpen: boolean) => void;
  setShowRecentSearches: (show: boolean) => void;
  onSelectResult: (result: AddressResult) => void;
  onSelectRecentSearch: (search: SearchHistoryItem) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

interface UseSearchKeyboardReturn {
  handleKeyDown: (event: React.KeyboardEvent) => void;
}

export const useSearchKeyboard = ({
  isOpen,
  results,
  recentSearches,
  showRecentSearches,
  selectedIndex,
  setSelectedIndex,
  setIsOpen,
  setShowRecentSearches,
  onSelectResult,
  onSelectRecentSearch,
  inputRef,
}: UseSearchKeyboardProps): UseSearchKeyboardReturn => {
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    const totalItems = showRecentSearches ? recentSearches.length : results.length;

    if (!isOpen || totalItems === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(selectedIndex < totalItems - 1 ? selectedIndex + 1 : 0);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(selectedIndex > 0 ? selectedIndex - 1 : totalItems - 1);
        break;
      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < totalItems) {
          if (showRecentSearches) {
            const selectedRecentSearch = recentSearches[selectedIndex];
            if (selectedRecentSearch) {
              onSelectRecentSearch(selectedRecentSearch);
            }
          } else {
            const selectedResult = results[selectedIndex];
            if (selectedResult) {
              onSelectResult(selectedResult);
            }
          }
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setShowRecentSearches(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  }, [
    isOpen,
    results,
    recentSearches,
    showRecentSearches,
    selectedIndex,
    setSelectedIndex,
    setIsOpen,
    setShowRecentSearches,
    onSelectResult,
    onSelectRecentSearch,
    inputRef,
  ]);

  return { handleKeyDown };
};